<script setup>
import { computed, nextTick, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { usePolicy } from 'dashboard/composables/usePolicy';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const CAPTAIN_CHILD_ROUTES = [
  'captain_assistants_overview_index', // Overview page
  'captain_assistants_train_index',
  'captain_assistants_brain_index',
  'captain_assistants_behavior_index',
  'captain_assistants_responses_index', // Faq page
  'captain_assistants_documents_index', // Document page
  'captain_assistants_scenarios_index', // Scenario page
  'captain_assistants_playground_index', // Playground page
  'captain_assistants_orders_index', // AI orders page
  'captain_assistants_inboxes_index', // Inboxes page
  'captain_tools_index', // Tools page
  'captain_assistants_settings_index', // Settings page
];

const FALLBACK_CAPTAIN_ROUTES = [
  'captain_assistants_playground_index',
  'captain_assistants_orders_index',
  'captain_assistants_inboxes_index',
  'captain_assistants_overview_index',
  'captain_assistants_train_index',
  'captain_assistants_brain_index',
  'captain_assistants_behavior_index',
  'captain_tools_index',
  'captain_assistants_settings_index',
];

const store = useStore();
const router = useRouter();
const { uiSettings } = useUISettings();
const route = useRoute();
const { shouldShow } = usePolicy();

const assistants = computed(
  () => store.getters['captainAssistants/getRecords']
);

const isAssistantPresent = assistantId => {
  return !!assistants.value.find(a => a.id === Number(assistantId));
};

const routeToView = (name, params) => {
  router.replace({ name, params, replace: true });
};

const generateRouterParams = () => {
  const { last_active_assistant_id: lastActiveAssistantId } =
    uiSettings.value || {};

  if (isAssistantPresent(lastActiveAssistantId)) {
    return {
      assistantId: lastActiveAssistantId,
    };
  }

  if (assistants.value.length > 0) {
    const { id: assistantId } = assistants.value[0];
    return { assistantId };
  }

  return null;
};

const canAccessCaptainRoute = name => {
  const matched = router.getRoutes().find(r => r.name === name);
  if (!matched) return false;

  return shouldShow(
    matched.meta?.featureFlag,
    matched.meta?.permissions,
    matched.meta?.installationTypes
  );
};

const firstAllowedCaptainRoute = () =>
  FALLBACK_CAPTAIN_ROUTES.find(name => canAccessCaptainRoute(name));

const routeToLastActiveAssistant = () => {
  const params = generateRouterParams();

  // No assistants found, redirect to create page
  if (!params) {
    return routeToView('captain_assistants_create_index', {
      accountId: route.params.accountId,
    });
  }

  const { navigationPath } = route.params;
  const isAValidRoute = CAPTAIN_CHILD_ROUTES.includes(navigationPath);
  const navigateTo =
    isAValidRoute && canAccessCaptainRoute(navigationPath)
      ? navigationPath
      : firstAllowedCaptainRoute();

  if (!navigateTo) return undefined;

  return routeToView(navigateTo, {
    accountId: route.params.accountId,
    ...params,
  });
};

const performRouting = async () => {
  await store.dispatch('captainAssistants/get');
  nextTick(() => routeToLastActiveAssistant());
};

onMounted(() => performRouting());
</script>

<template>
  <div
    class="flex items-center justify-center w-full bg-n-surface-1 text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
